/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';

type UseTimeComponentEventsOptions = {
  rootRef: RefObject<HTMLDivElement>;
  overlayControlsContainerRef: RefObject<HTMLDivElement>;
  isDisabled?: boolean;
};

/**
 * Hook that handles various Time Component gestures.
 */
const useTimeComponentEvents = ({
  rootRef,
  overlayControlsContainerRef,
  isDisabled = false
}: UseTimeComponentEventsOptions) => {
  const isFocused = useRef(false);

  const dispatchControlsShow = () => {
    const e = new Event('ControlsShow');
    overlayControlsContainerRef.current?.dispatchEvent(e);
  };

  const dispatchControlsHide = () => {
    const e = new Event('ControlsHide');
    overlayControlsContainerRef.current?.dispatchEvent(e);
  };

  const onFocus = () => {
    isFocused.current = true;
    dispatchControlsShow();
  };

  const onBlur = (e: FocusEvent) => {
    if (!e.relatedTarget || !rootRef.current?.contains(e.relatedTarget as Node)) {
      isFocused.current = false;
      dispatchControlsHide();
    }
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') {
      // Ensure component is focused upon interaction
      // e.g. on touch devices, if the component is not initially focused
      // then one tries to drag pan the component, the component is still
      // not focused. This is not a problem with mouse devices.
      rootRef.current?.focus();
    }
  };

  const onPointerOver = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') {
      dispatchControlsShow();
    }
  };

  const onPointerOut = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') {
      if (!isFocused.current) dispatchControlsHide();
    }
  };

  return isDisabled
    ? {}
    : {
        onFocus,
        onBlur,
        onPointerDown,
        onPointerOver,
        onPointerOut
      };
};

export { useTimeComponentEvents };
